import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, Type } from '@angular/core';
import { ColorPalette, PageSection } from '../../../models/end-client-reporting-package';
import { REPORT_BLOCK_REGISTRY, cardIsWashed, drawsOwnCards } from './report-block-registry';
import { ChartSeries, ReportBlock, StudyFacts, blockVariableKeys, chartColors } from '../report-aggregation';
import type { BlockPatch } from '../report-design-edit';
import type { ExploreHandle } from '../crosstab-explore';

@Component({
  selector: 'app-report-block-host',
  standalone: false,
  template: `
    <div class="report-block h-100"
         [class.is-editable]="editing"
         [class.is-bare]="selfCarded"
         [class.is-washed]="washedCard"
         [class.is-selected]="selected">
      <!-- The row exists when there is a title to show or edit chrome to hold, and it always carries
           a real gap so the chart never starts flush against it. No title and not editing means no
           row at all, and therefore no stray gap above the chart. -->
      @if (editing || block.header) {
        <div class="d-flex align-items-center mb-3">
          @if (editing) {
            <input type="text"
                   class="inline-edit block-title flex-fill"
                   maxlength="120"
                   placeholder="Card title (optional)"
                   aria-label="Card title"
                   [ngModel]="block.header ?? ''"
                   (ngModelChange)="headerChange.emit($event)">
          } @else {
            <h6 class="block-title flex-fill mb-0">{{ block.header }}</h6>
          }
          <!-- The deck projects the edit controls here, so they sit on the title row rather than in
               a strip of their own above the chart. -->
          <ng-content select="[blockActions]"></ng-content>
        </div>
      }

      @if (noQuestion) {
        <p class="block-note mb-0">
          No question chosen yet — click the card to pick one.
        </p>
      } @else if (notAsked) {
        <p class="block-note mb-0">Not asked</p>
      } @else if (unknownVariables) {
        <p class="block-note mb-0">No data for {{ missingVariables }}.</p>
      } @else if (component) {
        <ng-container *ngComponentOutlet="component; inputs: inputs"></ng-container>
        @if (caption) {
          <p class="block-note mb-0 mt-2">{{ caption }}</p>
        }
      } @else {
        <p class="block-note mb-0">No component registered for "{{ block.reportType }}".</p>
      }
    </div>
  `,
  styles: [`
    /* The card every module sits in. It wraps the whole block -- title row included -- so the
       heading reads as part of the card rather than as a label floating on the page behind it.
       Before this, .report-block had no rule at all: what looked like a card was only the chart
       SVG's own white plot area, which left every non-chart module sitting on bare background. */
    /* Positioned so the edit grip can sit just outside it, in the gutter to its left. */
    .report-block {
      position: relative;
      background-color: var(--ecr-card, #ffffff);
      border: 1px solid var(--ecr-line, #dbe4e8);
      border-radius: var(--ecr-radius, 8px);
      padding: 16px 18px;
    }

    /* In edit mode a card is clicked to open its settings, so it says so on hover: a faint ring in
       the editing tools' navy, a lighter step of the one an open card wears. */
    .report-block.is-editable:hover:not(.is-selected) {
      box-shadow: 0 0 0 1px var(--nav-grey3);
      cursor: pointer;
    }

    /* The wash, for a block whose own stages are the white paper -- see cardIsWashed. No border
       either: the white cards inside are what has edges here, and outlining the wash as well drew
       a box around a group of boxes. */
    /* The card whose settings are open in the deck's side panel, so it is plain which one the
       panel is changing. A ring rather than a border, so the card does not shift by its width. In
       Navigator's navy, the colour of the editing tools, not the report's. */
    .report-block.is-selected {
      box-shadow: 0 0 0 2px var(--nav-secondary);
    }

    .report-block.is-washed {
      background-color: var(--ecr-bg, #eef3f5);
      border: 0;
    }

    /* A block that supplies its own cards gets no card of its own -- see SELF_CARDED_BLOCKS. The
       edit chrome above it stays, because the block is still edited as one thing. */
    .report-block.is-bare {
      background-color: transparent;
      border: 0;
      padding: 0;
    }

    /* The card heading: a step up from the old 14px so it holds the top of the card, and the
       deck's own lead colour. Sized here rather than with .nav-fontNN because that scale has no
       15px step and its !important would block this anyway.

       The heading renders as an h6 when read and an input while editing, so both need the rule.
       !important because projects/main/src/styles.scss sizes every h1-h6 with !important, which no selector can outrank without one of its own -- the h6 was coming out at 12px. */
    h6.block-title,
    input.block-title {
      color: var(--ecr-primary, #13455B);
      font-size: var(--ecr-font-card, 15px) !important;
      font-weight: 700;
      line-height: 1.3;
    }

    /* Captions and the "nothing to draw" lines. The prototype's note size -- a step below the
       chart's own labels, so it reads as a footnote to the card rather than as part of it. */
    .block-note {
      color: var(--ecr-mut, #5a6b73);
      font-size: var(--ecr-font-xs, 11px);
    }

    /* Same rule as the deck's .inline-edit. Duplicated rather than shared because view
       encapsulation stops the deck's stylesheet reaching in here, and a global utility for one
       feature is a wider blast radius than six lines of CSS. */
    .inline-edit {
      background: transparent;
      border: 1px solid var(--ecr-line, #dbe4e8);
      border-radius: 3px;
      padding: 0 2px;
    }
    .inline-edit:hover {
      border-color: var(--ecr-grey, #c3ced3);
    }
    .inline-edit:focus {
      background: var(--ecr-card, #ffffff);
      border-color: var(--ecr-mut, #5a6b73);
      outline: none;
    }
    /* Every chart in the deck, wherever it is nested -- one rule rather than one per block
       component. The card behind it is what is rounded and white now, so the chart stops drawing
       its own panel: rounding it again would show a white box inside a white box, one corner
       radius out of step with the card's. */
    :host ::ng-deep highcharts-chart {
      display: block;
    }
    :host ::ng-deep .highcharts-background {
      fill: transparent;
    }
    /* The app-wide theme sets .highcharts-point { fill-opacity: 0.2 }, and CSS beats the fill
       attribute Highcharts writes, so no chart option can undo it. Scoped here so the rest of
       Navigator keeps its softer charts.

       Note it also sets rx/ry there. That does nothing to a bar, which is always drawn as a path
       rather than a rect -- its corner comes from BAR_BORDER_RADIUS in chart-layout.ts instead --
       but it is not dead: see the legend rule below. */
    :host ::ng-deep .highcharts-point {
      fill-opacity: 1;
    }
    :host ::ng-deep .highcharts-point.chart-track {
      fill-opacity: 0.15;
    }
    /* The same app-wide rule also sets rx/ry to 8, and a legend key *is* a real rect -- so unlike
       a bar, which is a path and ignores it, the key was rounded into a circle whatever symbolRadius
       a module asked for, CSS beating the attribute Highcharts writes from it. Squared back here,
       once, rather than per module: the modules set the option and it should be what decides. */
    :host ::ng-deep .highcharts-legend-item rect.highcharts-point {
      rx: 0;
      ry: 0;
    }
  `]
})
export class ReportBlockHostComponent implements OnChanges {
  @Input({ required: true }) block!: PageSection;
  @Input({ required: true }) series!: ChartSeries[];
  @Input({ required: true }) palette!: ColorPalette;
  @Input() alignCount?: number;
  @Input() compact?: boolean;
  /** Only the normal page layout passes this; compare mode renders one block many times. */
  @Input() editing = false;
  /** Whether this block's settings are the ones open in the side panel. */
  @Input() selected = false;
  @Input() studyFacts?: StudyFacts;

  /** True where the block draws a card per section itself, so this one must not draw another. */
  get selfCarded(): boolean {
    return drawsOwnCards(this.block);
  }

  /** True where the block's own content is white, so this card is the wash it sits on. */
  get washedCard(): boolean {
    return cardIsWashed(this.block);
  }

  /**
   * Explainer only: its headings come from the ladder it belongs to, so they are shown rather than
   * edited here. Its bullet points stay editable -- they are the half it still owns.
   */
  @Input() contentDerived?: boolean;
  /** Crosstab only, and only where a reader can drill into it. */
  @Input() explore?: ExploreHandle;

  @Output() headerChange = new EventEmitter<string>();
  @Output() blockPatch = new EventEmitter<BlockPatch>();

  component: Type<ReportBlock> | null = null;
  inputs: Record<string, unknown> = {};

  notAsked = false;
  /** A blank chart, added from the page header and not yet pointed at a question. */
  noQuestion = false;
  unknownVariables = false;
  missingVariables = '';
  caption = '';

  constructor(private host: ElementRef<HTMLElement>) { }

  ngOnChanges() {
    // Ten distinct colours, not the five roles cycled twice -- these back the .highcharts-color-N
    // classes, which beat a point's own `color` attribute, so slots 5-9 repeating slots 0-4 made a
    // chart draw two different things identically however the points were coloured.
    const roles = chartColors(this.palette, 10);
    for (let i = 0; i < 10; i++) {
      this.host.nativeElement.style.setProperty(`--highcharts-color-${i}`, roles[i]);
    }

    this.component = REPORT_BLOCK_REGISTRY[this.block.reportType] ?? null;

    this.inputs = {
      block: this.block,
      series: this.series,
      palette: this.palette,
      alignCount: this.alignCount,
      compact: this.compact,
      studyFacts: this.studyFacts,
      editing: this.editing,
      contentDerived: this.contentDerived,
      onPatch: (patch: BlockPatch) => this.blockPatch.emit(patch),
      // Only the table takes it; passed to anything else, ngComponentOutlet would refuse the input.
      ...(this.block.reportType === 'crosstab' ? { explore: this.explore } : {}),
    };

    const series = this.series ?? [];
    // A module that charts no question -- methodology -- is in none of these states. Every one of
    // them is an every() over the series, and [].every() is TRUE, so without this each would fire in
    // turn and replace the module with an empty-state message. Derived once rather than repeated per
    // check, because the third one is exactly what got missed when this was three separate guards.
    const keys = blockVariableKeys(this.block);
    const charts = keys.length > 0;
    this.noQuestion = charts && keys.every(key => !key);
    this.unknownVariables = charts && !this.noQuestion
      && (!series.length || series.every(s => !s.known));
    this.notAsked = charts && !this.unknownVariables && series.every(s => s.base === 0);
    this.missingVariables = series.map(s => s.name).join(', ');
    this.caption = this.captionFor();
  }

  private captionFor(): string {
    if (!this.series?.length || this.notAsked || this.unknownVariables) return '';

    // Any series rather than the first: every block that truncates puts the same figures on all of
    // them, and the first one can be a question that did not resolve and so carries none.
    const truncated = this.series.find(s => s.truncated)?.truncated;
    return truncated ? `top ${truncated.shown} of ${truncated.total} options` : '';
  }
}
